/**
 * upcomingAppointments — the shared appointment derivations (Soak #2 Wave
 * A, G1). One source of truth for "upcoming", consumed by the Overview
 * rail, the record hero band, and the Face Sheet.
 *
 * Two derivations, both keeping the stale-past-'scheduled' exclusion (a
 * dated 'scheduled' row whose date has passed with no attended/missed note
 * is the latent bug — never rendered as upcoming):
 *
 * - nextMonthAppointments (the LIST — Overview + Face Sheet): active +
 *   scheduled, either DATED within [today, today + 1 month] OR
 *   dateText-only (an undateable scheduled row can't be windowed, so it is
 *   still shown — dropping it on an emergency Face Sheet would be clinical
 *   info loss). Dated rows sort first (soonest), dateText-only rows last.
 *
 * - soonestUpcomingAppointment (the single HERO fact): the soonest active +
 *   scheduled row with NO upper window bound — so a known future
 *   appointment beyond the month still shows, never a false "None
 *   scheduled". Dated rows preferred (soonest), a dateText-only row as the
 *   fallback.
 *
 * Recurrence (Wave B / G2, 2026-07-12): a recurring appointment is ONE row
 * representing the series — never synthesized instances. It surfaces ONCE
 * with its cadence tag (the founder's "show only the next recurring and
 * indicate recurring status"), and is EXEMPT from the stale-past exclusion
 * (a scheduled recurring row repeats, so a passed anchor date does not make
 * it stale) and from the next-month window (its cadence lands within the
 * month). The card/rail/hero render the cadence tag from recurrenceTagLabel.
 */

import {
  appointmentDatePassed,
  isRecurring,
  nextOccurrence,
  type AppointmentRow,
} from '@/lib/clientRecord';

/** `today` ('YYYY-MM-DD') plus one calendar month, as 'YYYY-MM-DD'.
 *  Uses UTC construction from the date parts so it never drifts by the
 *  viewer's timezone (the parts are a calendar day, not an instant). */
export function oneMonthOut(todayIso: string): string {
  const [y, m, d] = todayIso.split('-').map(Number);
  // Date.UTC rolls month 13 → next year and clamps a short target month
  // (e.g. Jan 31 + 1mo → Mar 3) the same way the platform Date does.
  const dt = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1 + 1, d ?? 1));
  return dt.toISOString().slice(0, 10);
}

/** Active + scheduled + NOT stale-past. A dated row with scheduledFor in
 *  the past is excluded; a dateText-only row can't be judged and stays.
 *  A recurring series is EXEMPT from the stale-past exclusion (it repeats —
 *  a passed anchor date does not end it). */
function isLiveScheduled(a: AppointmentRow, today: string): boolean {
  if (!a.isActive || a.status !== 'scheduled') return false;
  if (isRecurring(a)) return true;
  return !appointmentDatePassed(a, today);
}

/** The EFFECTIVE date a row sorts/displays by: a recurring series' NEXT
 *  occurrence (never its stale anchor), a one-off's scheduledFor, or
 *  undefined for a dateText-only row. */
function effectiveDate(a: AppointmentRow, today: string): string | undefined {
  return nextOccurrence(a, today);
}

/** Soonest EFFECTIVE date first; a dateText-only row (no anchor) sorts LAST.
 *  Sorting by the effective date is what keeps a weekly series anchored in
 *  the past from out-ranking a real near-future one-off. */
function bySoonest(a: AppointmentRow, b: AppointmentRow, today: string): number {
  // '￿' sorts after any real ISO date, so undated rows fall to the end.
  // 0218: on the same day, the earlier time first and an untimed row last
  // ('~' sorts after any 'HH:MM').
  const key = (r: AppointmentRow): string => {
    const date = effectiveDate(r, today);
    return date === undefined || date === null ? '￿' : `${date}T${r.scheduledTime ?? '~'}`;
  };
  // Code-unit order, not localeCompare: collation ranks '~' before digits.
  const ka = key(a);
  const kb = key(b);
  return ka < kb ? -1 : ka > kb ? 1 : 0;
}

/**
 * The month's upcoming appointments (the LIST): live-scheduled rows that
 * are either dated within [today, today + 1 month] OR dateText-only.
 * Dated first (soonest), dateText-only last.
 */
export function nextMonthAppointments(
  appointments: readonly AppointmentRow[],
  today: string,
): AppointmentRow[] {
  const horizon = oneMonthOut(today);
  return appointments
    .filter((a) => {
      if (!isLiveScheduled(a, today)) return false;
      // A recurring series surfaces ONCE regardless of its anchor date —
      // its cadence lands within the month (never synthesize instances).
      if (isRecurring(a)) return true;
      // Dateless (dateText-only) scheduled rows can't be windowed — keep
      // them (never silently drop clinical info); dated rows must fall
      // inside the next-month window.
      if (typeof a.scheduledFor !== 'string') return true;
      return a.scheduledFor >= today && a.scheduledFor <= horizon;
    })
    .sort((a, b) => bySoonest(a, b, today));
}

/**
 * The single soonest upcoming appointment (the HERO fact): live-scheduled,
 * NO upper window bound (a known future appointment beyond the month still
 * shows), soonest dated preferred, a dateText-only row as the fallback.
 */
export function soonestUpcomingAppointment(
  appointments: readonly AppointmentRow[],
  today: string,
): AppointmentRow | null {
  return (
    appointments.filter((a) => isLiveScheduled(a, today)).sort((a, b) => bySoonest(a, b, today))[0] ??
    null
  );
}
